import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode
} from "react";
import PropTypes from "prop-types";
import { BreadcrumbData } from "../../Layout";

const defaultContext: State = { items: [], removeBreadcrumb: () => {}, setBreadcrumb: () => {} };
const BreadcrumbContext = createContext(defaultContext);

type Props = {
  children: ReactNode;
};

type State = {
  items: BreadcrumbData[];
  setBreadcrumb: (item: BreadcrumbData) => void;
  removeBreadcrumb: (key: string) => void;
};

export const BreadcrumbProvider = (props: Props) => {
  const [itemMap, setItemMap] = useState<Record<string, BreadcrumbData>>({});

  const setBreadcrumb = useCallback((item: BreadcrumbData) => {
    setItemMap(s => ({ ...s, [item.key]: item }));
  }, []);

  const removeBreadcrumb = useCallback((key: string) => {
    setItemMap(s => {
      const newItemMap = { ...s };
      delete newItemMap[key as keyof typeof newItemMap];
      return newItemMap;
    });
  }, []);

  const [stateValue, setStateValue] = useState({
    items: [] as BreadcrumbData[],
    removeBreadcrumb,
    setBreadcrumb
  });

  useEffect(() => {
    const items: any[] = Object.values(itemMap).sort((a: any, b: any) => a.order - b.order);
    setStateValue(s => ({ ...s, items }));
  }, [itemMap]);

  return (
    <BreadcrumbContext.Provider value={stateValue as State}>
      {props.children}
    </BreadcrumbContext.Provider>
  );
};

BreadcrumbProvider.propTypes = {
  children: PropTypes.node
};

BreadcrumbProvider.defaultProps = { children: null };

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbContext);

  if (!context) {
    throw new Error("useBreadcrumbs must be used in a BreadcrumbsProvider");
  }

  return context;
};
